import { ArabicField } from '../../components/admin/TranslateButton';
import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { sawiaAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

const EMPTY = { name:'', name_ar:'', description:'', address:'', city:'', state:'', country:'Nigeria', latitude:'', longitude:'', phone:'', email:'', cover_url:'' };

export default function AdminSawiaPage() {
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);

  const fetch = () => sawiaAPI.getAll().then(r => setLocations(r.data.data || []));
  useEffect(fetch, []);

  const startEdit = (loc) => { setEditing(loc.id); setForm({...EMPTY,...loc}); setShowForm(true); };
  const resetForm = () => { setEditing(null); setForm(EMPTY); setShowForm(false); };
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await sawiaAPI.update(editing, form); toast.success('Location updated!'); }
      else { await sawiaAPI.create(form); toast.success('Location added!'); }
      resetForm(); fetch();
    } catch { toast.error('Failed to save.'); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    await sawiaAPI.delete(id); toast.success('Deleted.'); fetch();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-navy-800">Sawia Locations</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
          <PlusIcon className="h-4 w-4" /> Add Location
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 space-y-4 shadow-sm">
          <h3 className="font-semibold text-navy-700">{editing ? 'Edit Location' : 'New Location'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="form-label">Name (English) *</label><input required className="form-input" value={form.name} onChange={e=>set('name',e.target.value)} /></div>
            <ArabicField label="Name (Arabic)" value={form.name_ar} onChange={v=>set('name_ar',v)} sourceText={form.name} />
          </div>
          <div><label className="form-label">Description</label><textarea rows={3} className="form-input resize-none" value={form.description} onChange={e=>set('description',e.target.value)} /></div>
          <div><label className="form-label">Address</label><input className="form-input" value={form.address} onChange={e=>set('address',e.target.value)} /></div>
          <div className="grid md:grid-cols-3 gap-4">
            <div><label className="form-label">City</label><input className="form-input" value={form.city} onChange={e=>set('city',e.target.value)} /></div>
            <div><label className="form-label">State</label><input className="form-input" value={form.state} onChange={e=>set('state',e.target.value)} /></div>
            <div><label className="form-label">Country</label><input className="form-input" value={form.country} onChange={e=>set('country',e.target.value)} /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="form-label">Latitude</label><input type="number" step="any" className="form-input" value={form.latitude} onChange={e=>set('latitude',e.target.value)} placeholder="e.g. 7.3775" /></div>
            <div><label className="form-label">Longitude</label><input type="number" step="any" className="form-input" value={form.longitude} onChange={e=>set('longitude',e.target.value)} placeholder="e.g. 3.9470" /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e=>set('phone',e.target.value)} /></div>
            <div><label className="form-label">Email</label><input type="email" className="form-input" value={form.email} onChange={e=>set('email',e.target.value)} /></div>
          </div>
          <div><label className="form-label">Cover Image URL</label><input className="form-input" value={form.cover_url} onChange={e=>set('cover_url',e.target.value)} placeholder="https://..." /></div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-5">{saving?'Saving...':(editing?'Update':'Add Location')}</button>
            <button type="button" onClick={resetForm} className="btn-outline text-sm py-2 px-5">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {locations.map(loc => (
          <div key={loc.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {loc.cover_url && <img src={loc.cover_url} alt={loc.name} className="w-full h-32 object-cover" />}
            {!loc.cover_url && <div className="h-20 bg-gradient-to-r from-navy-600 to-navy-800 flex items-center justify-center text-3xl">🕌</div>}
            <div className="p-4">
              <h3 className="font-bold text-navy-800">{loc.name}</h3>
              {loc.name_ar && <p className="arabic text-navy-600 text-sm">{loc.name_ar}</p>}
              <p className="text-gray-400 text-xs mt-1">📍 {[loc.city, loc.state, loc.country].filter(Boolean).join(', ')}</p>
              {loc.latitude && <p className="text-gray-300 text-xs mt-0.5">{loc.latitude}, {loc.longitude}</p>}
              {loc.phone && <p className="text-gray-500 text-xs mt-1">📞 {loc.phone}</p>}
              <div className="flex gap-2 mt-3">
                <button onClick={() => startEdit(loc)} className="flex items-center gap-1 text-xs text-navy-600 hover:bg-navy-50 px-2 py-1 rounded-lg transition-all">
                  <PencilIcon className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => del(loc.id)} className="flex items-center gap-1 text-xs text-red-400 hover:bg-red-50 px-2 py-1 rounded-lg transition-all">
                  <TrashIcon className="h-3.5 w-3.5" /> Delete
                </button>
                {loc.latitude && (
                  <a href={`https://maps.google.com/?q=${loc.latitude},${loc.longitude}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-cyan-600 hover:bg-cyan-50 px-2 py-1 rounded-lg transition-all ml-auto">
                    Maps ↗
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
        {locations.length === 0 && <p className="col-span-full text-center text-gray-400 py-12">No locations added yet.</p>}
      </div>
    </AdminLayout>
  );
}
